import React, { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import ModalSurface, { isPhoneDialog } from '../shared/ModalSurface';

// BL-177 Step 2j (D): THE popup template for the whole platform. Every popup / modal starts here, so they all look and behave the same.
//
//   a medium raised 3D tile (.ia-popup3d family) · a coloured strip across the top · title + ONE-line subtitle · content rows ·
//   3D buttons bottom-right · Escape and the close button close it · focus is trapped inside and handed back when it closes.
//
// Usage:
//   <Modal3D title="Pass to…" subtitle="Hand this conversation to a colleague." onClose={close}
//            actions={<><Button3D onClick={close}>Cancel</Button3D><Button3D variant="go" onClick={pass}>Pass</Button3D></>}>
//     ...content rows...
//   </Modal3D>
//
// Props
//   title, subtitle        title (h2) and a single plain-English line under it.
//   stripe                 'peacock' (default: the fixed gradient rule) | 'teal' | 'aaa-teal' | 'gold' | 'orange' | 'red' | 'violet'.
//                          Use a single colour only when the colour carries meaning (gold = check this, red = a problem).
//   actions                the buttons, rendered bottom-right in the order given (Cancel first, the main action last).
//   onClose                called for Escape, the ✕ button and (when closeOnScrim) a press on the dimmed background.
//                          A decision popup that must be answered (Allow / Decline) passes dismissible={false}: no ✕, Escape and scrim do nothing.
//   role                   'dialog' (default) or 'alertdialog' for something that needs an answer.
//   onInteract             called on the first press / key / focus / touch / change inside the tile (a countdown stops itself with it).
//   initialFocusRef        what gets focus first (default: the first control inside, else the tile itself).
//   restoreFocus           optional (previouslyFocusedElement) => void: where focus goes when it closes, instead of straight back to the opener
//                          (the Self-Help hook of #656 uses it). Passed through to the shared phone dialog too.
//   portal                 render into document.body. Use it whenever an ancestor can be transformed / filtered (a fixed tile is then
//                          positioned against that ancestor, not the window). Default false so the tile stays inside its owner's DOM.
//   size                   'medium' (default) | 'small' | 'wide'.
//   phoneLayout            'fullscreen' (default: the shared phone layout below) | 'card': a short pop-up stays a centred card over the dimmed
//                          background on a phone too (✕ top-right, Escape, focus trapped here, as on desktop). Pair it with closeOnScrim.
//   testId / className     hooks for tests and layout of the tile itself.
//
// Built ON the shared dialog surface (src/components/shared/ModalSurface.jsx, Mobile responsive Step 2 / #652): it supplies the overlay, the
// role="dialog" panel, the phone full-screen layout (global.css [data-mobile-modal] rules: full screen under 768 px, the body scrolls, the actions
// stay visible, a 44 px ✕), and the phone focus / Escape / Tab handling. Modal3D adds the 3D look, the strip, the title / subtitle / actions
// layout (using the shared .ia-modal-title / .ia-modal-body / .ia-modal-actions hooks) and, on desktop, Escape, the focus trap and focus return.
// There is no second dialog system.
//
// Colours: the strip colours are the locked Peacock tokens (Design System v1.07); nothing here invents a hex.

// The strip colours live in modal3d.css, keyed by data-modal3d-stripe (a gradient with CSS variables cannot be set inline in every browser/test DOM).
// Exported here as the list of valid names, with each single colour's token for code that needs the hex.
export const MODAL3D_STRIPES = {
    peacock: 'gradient rule (magenta, violet, blue, cyan)',
    teal: '#03B7C9',
    'aaa-teal': '#006277',
    gold: '#F1C61E',
    orange: '#FA8A2E',
    red: '#FF0000',
    violet: '#5A2D87',
};

const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusables(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter((node) => !node.hasAttribute('hidden') && node.getAttribute('aria-hidden') !== 'true');
}

export default function Modal3D({
    title,
    subtitle = '',
    stripe = 'peacock',
    size = 'medium',
    role = 'dialog',
    dismissible = true,
    closeOnScrim = false,
    closeLabel = 'Close',
    onClose,
    onInteract,
    initialFocusRef,
    restoreFocus,
    portal = false,
    actions = null,
    children,
    testId = 'modal3d',
    className = '',
    theme = 'dark',
    phoneLayout = 'fullscreen',
    ...rest
}) {
    const phoneCard = phoneLayout === 'card';
    // True while the shared phone full-screen layout owns focus, Escape and Tab (never for a phone card).
    const phoneOwned = () => !phoneCard && isPhoneDialog(tileRef);
    const titleId = useId();
    const subtitleId = useId();
    const tileRef = useRef(null);
    const returnFocusTo = useRef(null);
    const settling = useRef(false); // the focus we place on open is not the person's doing, so it must not count as an interaction
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    const restoreFocusRef = useRef(restoreFocus);
    useEffect(() => { restoreFocusRef.current = restoreFocus; }, [restoreFocus]);

    // focus in on open, hand it back on close (on a phone the shared surface does both)
    useEffect(() => {
        const tile = tileRef.current;
        if (phoneOwned()) return undefined;
        returnFocusTo.current = typeof document !== 'undefined' ? document.activeElement : null;
        const target = initialFocusRef?.current || focusables(tile).find((node) => node.getAttribute('data-modal3d-close') !== 'true') || tile;
        settling.current = true;
        if (target && typeof target.focus === 'function') target.focus();
        settling.current = false;
        return () => {
            const back = returnFocusTo.current;
            if (restoreFocusRef.current) restoreFocusRef.current(back);
            else if (back && typeof back.focus === 'function' && document.contains(back)) back.focus();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only on open
    }, []);

    // A phone card (phoneLayout="card") is not the shared full-screen layout, so that layout's page scroll lock never ran for it and the
    // page behind the card could scroll (Click-to-call review). Lock it here too, at phone width only, and put it back on close.
    useEffect(() => {
        if (!phoneCard || typeof window === 'undefined' || !window.matchMedia?.('(max-width: 767px)')?.matches) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [phoneCard]);

    const close = useCallback(() => { if (dismissible) onCloseRef.current?.(); }, [dismissible]);

    const onKeyDown = (event) => {
        if (phoneOwned()) return; // the shared phone dialog handles Escape and Tab
        if (event.key === 'Escape') {
            if (!dismissible) return;
            event.stopPropagation();
            event.preventDefault();
            close();
            return;
        }
        if (event.key !== 'Tab') return;
        const nodes = focusables(tileRef.current);
        if (!nodes.length) { event.preventDefault(); tileRef.current?.focus(); return; }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === tileRef.current || !tileRef.current.contains(active))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (active === last || !tileRef.current.contains(active))) { event.preventDefault(); first.focus(); }
    };

    const interact = onInteract ? () => { if (!settling.current) onInteract(); } : undefined;

    const tile = (
        <ModalSurface
            onClose={close}
            closeDisabled={!dismissible}
            label={title}
            labelledBy={titleId}
            role={role}
            restoreFocus={restoreFocus}
            panelRef={tileRef}
            phoneFullScreen={!phoneCard}
            overlayClassName="ia-modal3d-scrim"
            panelClassName={`ia-d2 ia-popup3d ia-modal3d ia-modal3d--${size} ${className}`}
            onOverlayClick={closeOnScrim && dismissible ? close : undefined}
            overlayProps={{ 'data-testid': `${testId}-scrim` }}
            panelProps={{
                tabIndex: -1,
                'aria-describedby': subtitle ? subtitleId : undefined,
                'data-testid': testId,
                'data-modal3d': 'true',
                'data-modal3d-size': size,
                'data-modal3d-phone': phoneCard ? 'card' : undefined,
                'data-theme': theme,
                onKeyDown,
                onPointerDownCapture: interact,
                onKeyDownCapture: interact,
                onFocusCapture: interact,
                onTouchStartCapture: interact,
                onClickCapture: interact,
                onChangeCapture: interact,
                ...rest,
            }}
        >
            <div className="ia-modal3d__stripe" data-modal3d-stripe={MODAL3D_STRIPES[stripe] ? stripe : 'peacock'} aria-hidden="true" />
            <div className="ia-modal3d__body ia-modal-layout">
                <div className="ia-modal3d__head">
                    <div className="ia-modal3d__titles">
                        <h2 id={titleId} className="ia-modal3d__title ia-modal-title">{title}</h2>
                        {subtitle && <p id={subtitleId} className="ia-modal3d__subtitle">{subtitle}</p>}
                    </div>
                    {dismissible && (
                        <button type="button" className="ia-modal3d__close ia-modal-desktop-close" aria-label={closeLabel} data-modal3d-close="true" onClick={close}>×</button>
                    )}
                </div>
                <div className="ia-modal3d__content ia-modal-body">{children}</div>
                {actions && <div className="ia-modal3d__actions ia-modal-actions" data-modal3d-actions="true">{actions}</div>}
            </div>
        </ModalSurface>
    );

    return portal && typeof document !== 'undefined' ? createPortal(tile, document.body) : tile;
}
